import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, X, Check, Flame, Target, Coffee, Sun, Moon, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getSettings, getMealsByDate, saveMealLog, deleteMealLog } from '../lib/db';
import { MEAL_TEMPLATES, getDateString, formatDate, getDayType } from '../lib/constants';

export const Meals = () => {
  const [settings, setSettings] = useState(null);
  const [meals, setMeals] = useState([]);
  const [showCustom, setShowCustom] = useState(false);
  const [customMeal, setCustomMeal] = useState({ name: '', calories: '', protein: '' });
  const [loading, setLoading] = useState(true);

  const today = getDateString();

  const loadData = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
      
      const todayMeals = await getMealsByDate(today);
      setMeals(todayMeals);
    } catch (error) {
      console.error('Error loading meals:', error);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const logMeal = async (template, category) => {
    const meal = {
      id: uuidv4(),
      date: today,
      templateId: template.id,
      name: template.name,
      shortName: template.shortName,
      calories: template.calories,
      protein: template.protein,
      category,
      timestamp: new Date().toISOString(),
    };
    
    await saveMealLog(meal);
    setMeals(prev => [...prev, meal]);
    toast.success(`${template.shortName} logged!`);
  };

  const logCustomMeal = async () => {
    if (!customMeal.name || !customMeal.calories) {
      toast.error('Enter name and calories');
      return;
    }

    const meal = {
      id: uuidv4(),
      date: today,
      templateId: 'custom',
      name: customMeal.name,
      shortName: customMeal.name,
      calories: parseInt(customMeal.calories) || 0,
      protein: parseInt(customMeal.protein) || 0,
      category: 'custom',
      timestamp: new Date().toISOString(),
    };
    
    await saveMealLog(meal);
    setMeals(prev => [...prev, meal]);
    setCustomMeal({ name: '', calories: '', protein: '' });
    setShowCustom(false);
    toast.success('Custom meal logged!');
  };

  const removeMeal = async (id) => {
    await deleteMealLog(id);
    setMeals(prev => prev.filter(m => m.id !== id));
    toast.success('Meal removed');
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalCalories = meals.reduce((sum, m) => sum + m.calories, 0);
  const totalProtein = meals.reduce((sum, m) => sum + m.protein, 0);
  const caloriePercent = Math.min((totalCalories / settings.targetCalories) * 100, 100);
  const proteinPercent = Math.min((totalProtein / settings.targetProtein) * 100, 100);

  const dayType = getDayType(today, settings.startDate);
  const isCheatDay = dayType === 'cheat';

  return (
    <div className="space-y-6" data-testid="meals-page">
      {/* Header */}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
          {formatDate(today)}
        </p>
        <h1 className="text-2xl font-black tracking-tight">Meals</h1>
        <p className="text-xs text-gray-500 mt-1">Tap a meal to log — calories & protein add up automatically</p>
      </div>

      {/* Cheat Day Banner */}
      {isCheatDay && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4" data-testid="cheat-meal-banner">
          <p className="text-orange-400 font-bold text-sm">
            🔥 Cheat Day! Remember: 60 air squats + ½ tsp cinnamon before each meal
          </p>
        </div>
      )}

      {/* Daily Totals */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5" data-testid="daily-totals">
        <h2 className="text-sm font-black uppercase tracking-wider mb-4">Daily Totals</h2>
        
        <div className="space-y-4">
          {/* Calories Bar */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <Flame size={16} className="text-orange-400" />
                <span className="text-sm font-bold">Calories</span>
              </div>
              <span className="font-mono font-bold">
                <span className={totalCalories >= settings.targetCalories ? 'text-[#00E676]' : ''}>
                  {totalCalories}
                </span>
                <span className="text-gray-500">/{settings.targetCalories}</span>
              </span>
            </div>
            <div className="h-3 bg-[#161625] rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  totalCalories > settings.targetCalories 
                    ? 'bg-red-500' 
                    : 'bg-orange-400'
                }`}
                style={{ width: `${caloriePercent}%` }}
              />
            </div>
          </div>

          {/* Protein Bar */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <Target size={16} className="text-[#00E676]" />
                <span className="text-sm font-bold">Protein</span>
              </div>
              <span className="font-mono font-bold">
                <span className={totalProtein >= settings.targetProtein ? 'text-[#00E676]' : ''}>
                  {totalProtein}g
                </span>
                <span className="text-gray-500">/{settings.targetProtein}g</span>
              </span>
            </div>
            <div className="h-3 bg-[#161625] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#00E676] transition-all"
                style={{ width: `${proteinPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Logged Meals */}
      {meals.length > 0 && (
        <div className="space-y-2" data-testid="logged-meals">
          <h3 className="text-sm font-black uppercase tracking-wider text-gray-500">Logged Today</h3>
          {meals.map(meal => (
            <div 
              key={meal.id}
              className="flex items-center justify-between bg-[#24243e] border border-white/5 rounded-xl p-3"
              data-testid={`logged-meal-${meal.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#161625] flex items-center justify-center">
                  {meal.category === 'breakfast' && <Coffee size={18} className="text-yellow-400" />}
                  {meal.category === 'lunch' && <Sun size={18} className="text-orange-400" />}
                  {meal.category === 'dinner' && <Moon size={18} className="text-blue-400" />}
                  {meal.category === 'snacks' && <Cookie size={18} className="text-pink-400" />}
                  {meal.category === 'custom' && <Plus size={18} className="text-gray-400" />}
                </div>
                <div>
                  <p className="font-medium text-sm">{meal.shortName}</p>
                  <p className="text-xs text-gray-500">{meal.calories}cal • {meal.protein}g protein</p>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeMeal(meal.id)}
                className="h-8 w-8 text-gray-500 hover:text-red-500 hover:bg-red-500/10"
                data-testid={`remove-meal-${meal.id}`}
              >
                <X size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Meal Templates */}
      <div className="space-y-4">
        {/* Breakfast */}
        <MealCategory 
          title="Breakfast" 
          icon={<Coffee className="text-yellow-400" />}
          templates={MEAL_TEMPLATES.breakfast}
          onSelect={(t) => logMeal(t, 'breakfast')}
          loggedMeals={meals}
        />

        {/* Lunch */}
        <MealCategory 
          title="Lunch" 
          icon={<Sun className="text-orange-400" />}
          templates={MEAL_TEMPLATES.lunch}
          onSelect={(t) => logMeal(t, 'lunch')}
          loggedMeals={meals}
        />

        {/* Dinner */}
        <MealCategory 
          title="Dinner" 
          icon={<Moon className="text-blue-400" />}
          templates={MEAL_TEMPLATES.dinner}
          onSelect={(t) => logMeal(t, 'dinner')}
          loggedMeals={meals}
        />

        {/* Snacks */}
        <MealCategory 
          title="Snacks" 
          icon={<Cookie className="text-pink-400" />}
          templates={MEAL_TEMPLATES.snacks}
          onSelect={(t) => logMeal(t, 'snacks')}
          loggedMeals={meals}
        />
      </div>

      {/* Custom Meal */}
      <Dialog open={showCustom} onOpenChange={setShowCustom}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full border-dashed border-white/20 hover:border-[#00E676]/50 hover:bg-[#00E676]/5"
            data-testid="add-custom-meal-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Custom Meal
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-[#24243e] border-white/10">
          <DialogHeader>
            <DialogTitle>Custom Meal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              placeholder="Meal name"
              value={customMeal.name}
              onChange={(e) => setCustomMeal(prev => ({ ...prev, name: e.target.value }))}
              className="bg-[#161625] border-white/10"
              data-testid="custom-meal-name"
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                inputMode="numeric"
                placeholder="Calories"
                value={customMeal.calories}
                onChange={(e) => setCustomMeal(prev => ({ ...prev, calories: e.target.value }))}
                className="bg-[#161625] border-white/10"
                data-testid="custom-meal-calories"
              />
              <Input
                type="number"
                inputMode="numeric"
                placeholder="Protein (g)"
                value={customMeal.protein}
                onChange={(e) => setCustomMeal(prev => ({ ...prev, protein: e.target.value }))}
                className="bg-[#161625] border-white/10"
                data-testid="custom-meal-protein"
              />
            </div>
            <Button 
              onClick={logCustomMeal}
              className="w-full bg-[#00E676] text-black font-bold hover:bg-[#00c853]"
              data-testid="save-custom-meal-btn"
            >
              Log Meal
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MealCategory = ({ title, icon, templates, onSelect, loggedMeals }) => {
  return (
    <div data-testid={`meal-category-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-black uppercase tracking-wider">{title}</h3>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {templates.map(template => {
          const timesLogged = loggedMeals.filter(m => m.templateId === template.id).length;
          
          return (
            <button
              key={template.id}
              onClick={() => onSelect(template)}
              className="flex items-center justify-between p-3 bg-[#24243e] border border-white/5 rounded-xl hover:border-[#00E676]/30 active:scale-[0.98] transition-all"
              data-testid={`meal-template-${template.id}`}
            >
              <div className="text-left">
                <p className="font-medium text-sm">{template.shortName}</p>
                <p className="text-xs text-gray-500">{template.calories}cal • {template.protein}g</p>
              </div>
              <div className="flex items-center gap-2">
                {timesLogged > 0 && (
                  <span className="text-xs font-bold text-[#00E676] bg-[#00E676]/10 px-2 py-1 rounded">
                    ×{timesLogged}
                  </span>
                )}
                <Plus size={18} className="text-gray-500" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Meals;
